import { useCallback, useEffect, useRef, useState } from 'react';
import { FileTextIcon, StopCircleIcon, TrashIcon } from '@phosphor-icons/react';
import FileDropzone from './FileDropzone';
import FileRow from './FileRow';
import Modal from 'src/components/ui/Modal';
import Button from 'src/components/ui/Button';
import Checkbox from 'src/components/ui/Checkbox';
import { useUploadBatch } from '../useUploadBatch';
import { ACCEPTED_EXTENSIONS, isFailed, isReadyForUse, validationErrorMessage } from '../utils';
import BatchProgress from './BatchProgress';
import { useUploadBatchContext } from '../UploadBatchContext';
export type { BatchUploadFile } from '../types';
import type { BatchUploadFile } from '../types';

export interface BatchUploadResult {
  /** Files that passed security and are ready for use. */
  files: BatchUploadFile[];
}

export interface OrganisationUploadOption {
  /**
   * Whether to show the "Add to organisation library" checkbox. Callers should
   * only enable it for users allowed to add organisational documents (non-guests).
   */
  enabled: boolean;
  /** Initial checked state of the checkbox. Defaults to false. */
  defaultChecked?: boolean;
  /** Checkbox label. Defaults to "Add to organisation library". */
  label?: string;
  /** Helper text shown under the label. */
  description?: string;
}

interface BatchUploadModalProps {
  open: boolean;
  onClose: () => void;
  /** Called when all uploaded files are clean and in the secure container
   * (READY status). Failed files are excluded from fileIds.
   * The modal stays open — call onClose() inside this handler when ready. */
  onComplete: (result: BatchUploadResult) => void;
  title?: string;
  /** If true the modal cannot be dismissed while an upload is in progress. */
  disableCloseWhileUploading?: boolean;
  /** If true only a single file can be selected; dropping or picking more replaces the current selection. */
  singleFile?: boolean;
  /** Message shown in the footer after onComplete fires while waiting for the caller to navigate/close. */
  completingMessage?: string;
  /**
   * document_type tag for all uploaded files. Defaults to 'upload'.
   * Use 'tenant_file' for organisation uploads, 'reviewed_file' for reviews, etc.
   */
  documentType?: string;
  /** When set, the uploaded document is shared with this group after promotion. */
  groupId?: string;
  /** When set, the uploaded document is linked as PAM question evidence after promotion. */
  pamQuestionId?: string;
  pamSiteId?: string;
  /** Skip waiting for Document Intelligence before marking files ready. */
  skipDI?: boolean;
  /** Show a "select a folder" button that uploads all files inside it. */
  allowFolder?: boolean;
  /**
   * When true, the modal closes as soon as uploading begins and the
   * JobsWidget takes over. onComplete still fires when all
   * files finish so the caller can invalidate queries etc.
   */
  backgroundOnUpload?: boolean;
  /**
   * Called immediately when background uploading starts, with the opaque
   * per-file keys returned by {@link startBackgroundUpload}.
   */
  onStart?: (keys: string[]) => void;
  /**
   * When provided and ``enabled``, renders an "Add to organisation library"
   * checkbox. Checking it uploads files to the organisation library so they
   * become reusable organisational documents visible to the whole tenant.
   */
  organisationUpload?: OrganisationUploadOption;
}

export default function BatchUploadModal({
  open,
  onClose,
  onComplete,
  title = 'Upload files',
  disableCloseWhileUploading = true,
  singleFile = false,
  completingMessage = 'Processing…',
  documentType,
  groupId,
  pamQuestionId,
  pamSiteId,
  skipDI = true,
  allowFolder = false,
  backgroundOnUpload = false,
  onStart,
  organisationUpload
}: BatchUploadModalProps) {
  const [addToOrganisation, setAddToOrganisation] = useState(organisationUpload?.defaultChecked ?? false);
  const effectiveAddToOrganisation = organisationUpload?.enabled ? addToOrganisation : undefined;

  const {
    phase,
    localFiles,
    batchFiles,
    addFiles,
    removeLocalFile,
    startUpload: localStartUpload,
    stopUpload: localStopUpload,
    reset,
    clearTracking,
    uploading,
    uploadError
  } = useUploadBatch(
    documentType,
    { groupId, pamQuestionId, pamSiteId, addToOrganisation: effectiveAddToOrganisation },
    skipDI
  );

  const { startBackgroundUpload } = useUploadBatchContext();
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const handleAddFiles = useCallback(
    (incoming: File[]) => {
      if (singleFile) {
        localFiles.forEach((f) => removeLocalFile(f.key));
        addFiles([incoming[0]]);
      } else {
        addFiles(incoming);
      }
    },
    [singleFile, localFiles, removeLocalFile, addFiles]
  );

  // In background mode, delegate to the app-level context upload so the
  // upload survives navigation away from the page that opened this modal.
  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;

  const startUpload = useCallback(() => {
    if (backgroundOnUpload) {
      const valid = localFiles.filter((f) => !f.validationError && f.sha256);
      if (valid.length === 0) return;
      const keys = startBackgroundUpload(
        valid.map((f) => f.file),
        { documentType, groupId, pamQuestionId, pamSiteId, addToOrganisation: effectiveAddToOrganisation },
        (readyFiles) => onCompleteRef.current({ files: readyFiles })
      );
      onStartRef.current?.(keys);
      reset();
      onClose();
    } else {
      localStartUpload();
    }
  }, [
    backgroundOnUpload,
    localFiles,
    documentType,
    groupId,
    pamQuestionId,
    pamSiteId,
    effectiveAddToOrganisation,
    startBackgroundUpload,
    localStartUpload,
    reset,
    onClose
  ]);

  const stopUpload = useCallback(() => {
    localStopUpload();
  }, [localStopUpload]);

  const [completeFired, setCompleteFired] = useState(false);

  const validCount = localFiles.filter((f) => !f.validationError && f.sha256).length;
  const invalidCount = localFiles.filter((f) => f.validationError).length;

  // Fire onComplete (non-background path) once all files settle.
  useEffect(() => {
    if (backgroundOnUpload) return;
    if (completeFired) return;
    if (phase !== 'tracking') return;
    if (batchFiles.length === 0) return;

    const allSettled = batchFiles.every((f) => isReadyForUse(f.status) || isFailed(f.status));
    if (!allSettled) return;

    const readyFiles = batchFiles
      .filter((f) => isReadyForUse(f.status))
      .map((f) => ({ fileId: f.fileId, name: f.name, size: f.size }));
    setCompleteFired(true);
    clearTracking();
    onCompleteRef.current({ files: readyFiles });
  }, [backgroundOnUpload, phase, batchFiles, completeFired]);

  // Reset local state when modal closes so it's fresh next time.
  const handleClose = useCallback(() => {
    if (!backgroundOnUpload && disableCloseWhileUploading && phase === 'uploading') return;
    reset();
    setCompleteFired(false);
    onClose();
  }, [backgroundOnUpload, disableCloseWhileUploading, phase, reset, onClose]);

  // Also reset when the modal is re-opened.
  const prevOpen = useRef(open);
  useEffect(() => {
    if (open && !prevOpen.current) {
      reset();
      setCompleteFired(false);
      setAddToOrganisation(organisationUpload?.defaultChecked ?? false);
    }
    prevOpen.current = open;
  }, [open, reset, organisationUpload?.defaultChecked]);

  const isActive = phase === 'uploading' || phase === 'tracking';
  const acceptAttr = [...ACCEPTED_EXTENSIONS].map((ext) => `.${ext}`).join(',');

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      disableBackdropClick={disableCloseWhileUploading && phase === 'uploading'}
      className="w-[min(95vw,580px)] max-w-none"
    >
      <div className="flex flex-col gap-3 pt-2">
        <FileDropzone
          singleFile={singleFile}
          allowFolder={allowFolder}
          disabled={isActive}
          accept={acceptAttr}
          onFiles={handleAddFiles}
        />

        {/* ── Add to organisation library ── */}
        {organisationUpload?.enabled && (
          <label className="flex items-start gap-2.5 rounded-xl border border-border-default bg-white px-4 py-3 cursor-pointer">
            <Checkbox
              checked={addToOrganisation}
              disabled={isActive}
              onChange={(e) => setAddToOrganisation(e.target.checked)}
              className="mt-0.5"
            />
            <span className="flex flex-col">
              <span className="text-sm font-medium text-content-default">
                {organisationUpload.label ?? 'Add to Organisation Folder'}
              </span>
              <span className="text-xs text-content-subtle">
                {organisationUpload.description ??
                  'Everyone in your organisation will be able to find and reuse these files.'}
              </span>
            </span>
          </label>
        )}

        {/* ── File list (selection) ── */}
        {phase === 'selecting' && localFiles.length > 0 && (
          <div className="overflow-y-auto custom-scrollbar max-h-56">
            {localFiles.map((f) => (
              <FileRow
                key={f.key}
                name={f.name}
                size={f.size}
                icon={FileTextIcon}
                subtitle={
                  f.validationError ? (
                    <span className="text-misc-error">
                      {f.validationError ? validationErrorMessage(f.validationError) : 'Invalid'}
                    </span>
                  ) : undefined
                }
                actionIcon={TrashIcon}
                actionLabel={`Remove ${f.name}`}
                onAction={() => removeLocalFile(f.key)}
              />
            ))}
          </div>
        )}

        {phase === 'selecting' && invalidCount > 0 && (
          <p className="text-xs text-misc-error">
            {invalidCount} file{invalidCount > 1 ? 's' : ''} cannot be uploaded and will be skipped.
          </p>
        )}
        {phase === 'selecting' && uploadError && (
          <p className="text-sm text-misc-error bg-misc-errorlight rounded-lg px-4 py-3">{uploadError}</p>
        )}

        {/* ── File list (uploading / tracking) ── */}
        {isActive && <BatchProgress batchFiles={batchFiles} phase={phase} />}

        {/* ── Footer ── */}
        <div className="flex justify-end gap-2 pt-2">
          {completeFired ? (
            <Button variant="primary" size="md" loading disabled>
              {completingMessage}
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                size="md"
                onClick={handleClose}
                disabled={disableCloseWhileUploading && phase === 'uploading'}
              >
                {isActive ? 'Close' : 'Cancel'}
              </Button>
              {phase === 'uploading' && (
                <Button variant="outline" size="md" icon={StopCircleIcon} onClick={stopUpload}>
                  Stop upload
                </Button>
              )}
              {phase === 'selecting' && (
                <Button
                  variant="primary"
                  size="md"
                  onClick={startUpload}
                  disabled={uploading || validCount === 0}
                  loading={uploading}
                >
                  {validCount > 1 ? `Upload ${validCount} files` : validCount === 1 ? 'Upload file' : 'Upload files'}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
